import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { Chapter } from "./Chapter";
import { FAQS } from "@/data/content";

const setFaqJsonLd = () => {
  let tag = document.head.querySelector('script[data-jsonld="faq"]');
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-jsonld", "faq");
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });
};

export const Faq = () => {
  const [open, setOpen] = useState(0);

  useEffect(() => {
    setFaqJsonLd();
    return () => {
      const tag = document.head.querySelector('script[data-jsonld="faq"]');
      if (tag) tag.remove();
    };
    // Inject the FAQ JSON-LD once on mount; remove it on unmount so it never
    // leaks onto other routes. Intentional [].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="faq" className="relative z-10 py-24 md:py-32 lg:py-40" data-testid="faq-section">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <Chapter num="08" title="Questions" kicker="FAQ" />

        <div className="border-t border-[#d2d5ce] max-w-4xl">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.05}>
                <div className="border-b border-[#d2d5ce]">
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="group w-full flex items-start justify-between gap-6 py-6 md:py-7 text-left"
                  >
                    <span className="font-serif-display text-xl md:text-2xl text-[#16324f] leading-snug group-hover:text-[#b5423a] transition-colors duration-300">
                      {f.q}
                    </span>
                    <Plus
                      size={22}
                      strokeWidth={1.6}
                      className={`shrink-0 mt-1.5 text-[#b5423a] transition-transform duration-500 ${isOpen ? "rotate-45" : ""}`}
                    />
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-500 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                  >
                    <div className="overflow-hidden">
                      <p
                        data-testid={`faq-answer-${i}`}
                        className="pb-7 pr-6 md:pr-10 text-[16px] md:text-[17px] text-[#48606f] leading-relaxed max-w-3xl"
                      >
                        {f.a}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
